import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, updateDoc, doc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { useParams, useNavigate } from 'react-router-dom';

const AdminMessages = () => {
  const { id: messageIdFromUrl } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // all, new, read, replied

  const updateMessageStatus = useCallback(async (messageId, newStatus) => {
    try {
      const messageRef = doc(db, "messages", messageId);
      await updateDoc(messageRef, { status: newStatus });
      setMessages(prev => prev.map(m => 
        m.firestoreId === messageId ? { ...m, status: newStatus } : m
      ));
    } catch (err) {
      console.error("Error updating status:", err);
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const messagesQuery = query(
        collection(db, "messages"),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(messagesQuery);
      const messagesData = snapshot.docs.map(doc => ({
        firestoreId: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      setMessages(messagesData);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching messages:", err);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Sync selected message with URL
  useEffect(() => {
    if (messageIdFromUrl && messages.length > 0) {
      const msg = messages.find(m => m.firestoreId === messageIdFromUrl);
      if (msg) {
        setSelectedMessage(msg);
        if (msg.status === 'new') {
          updateMessageStatus(msg.firestoreId, 'read');
        }
      }
    } else if (!messageIdFromUrl) {
      setSelectedMessage(null);
    }
  }, [messageIdFromUrl, messages, updateMessageStatus]);

  const deleteMessage = async (messageId) => {
    if (window.confirm('Da li ste sigurni da želite obrisati ovu poruku?')) {
      try {
        await deleteDoc(doc(db, "messages", messageId));
        fetchMessages();
        navigate('/admin/messages');
      } catch (err) {
        console.error("Error deleting message:", err);
      }
    }
  };

  const openMessage = (message) => {
    navigate(`/admin/messages/view/${message.firestoreId}`);
  };

  const filteredMessages = messages.filter(msg => {
    if (filter === 'all') return true;
    return msg.status === filter;
  });

  const getStatusBadge = (status) => {
    const badges = {
      new: { text: 'Novo', color: 'bg-blue-50 text-blue-700' },
      read: { text: 'Pročitano', color: 'bg-gray-100 text-black' },
      replied: { text: 'Odgovoreno', color: 'bg-emerald-50 text-emerald-900' }
    };
    return badges[status] || badges.new;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('bs-BA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const newMessagesCount = messages.filter(m => m.status === 'new').length;

  if (loading) {
    return <div className="p-6">Učitavanje poruka...</div>;
  }

  return (
    <div className="flex bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden min-h-[600px] max-h-[calc(100vh-120px)] animate-fade-in font-sans">
      {/* SIDEBAR - Lista Poruka */}
      <div className={`w-full md:w-80 lg:w-96 bg-white border-r border-gray-100 flex flex-col ${selectedMessage ? 'hidden md:flex' : 'flex'}`}>
        {/* Header */}
        <div className="p-6 border-b border-gray-50 flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-black text-black tracking-tight uppercase">Poruke</h2>
            {newMessagesCount > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full">
                {newMessagesCount}
              </span>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="flex p-1 bg-gray-50 rounded-xl">
            <button
              onClick={() => setFilter('all')}
              className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
                filter === 'all' ? 'bg-white text-black shadow-sm' : 'text-black hover:text-gray-800'
              }`}
            >
              Sve
            </button>
            <button
              onClick={() => setFilter('new')}
              className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
                filter === 'new' ? 'bg-white text-black shadow-sm' : 'text-black hover:text-gray-800'
              }`}
            >
              Nove
            </button>
            <button
              onClick={() => setFilter('replied')}
              className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
                filter === 'replied' ? 'bg-white text-black shadow-sm' : 'text-black hover:text-gray-800'
              }`}
            >
              Odgovoreno
            </button>
          </div>
        </div>

        {/* Messages List */}
        <div className="flex-1 overflow-y-auto">
          {filteredMessages.length === 0 ? (
            <div className="p-12 text-center flex flex-col items-center justify-center gap-4">
              <span className="text-4xl opacity-20">📩</span>
              <p className="text-xs font-bold text-black uppercase tracking-widest">Nema poruka</p>
            </div>
          ) : (
            filteredMessages.map((msg) => (
              <div
                key={msg.firestoreId}
                onClick={() => openMessage(msg)}
                className={`p-6 border-b border-gray-50 cursor-pointer transition-all hover:bg-gray-50 group ${
                  selectedMessage?.firestoreId === msg.firestoreId ? 'bg-black text-white hover:bg-black' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className={`text-sm font-bold truncate pr-2 ${selectedMessage?.firestoreId === msg.firestoreId ? 'text-white' : 'text-black'}`}>
                    {msg.name}
                  </h3>
                  <span className={`text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${
                    selectedMessage?.firestoreId === msg.firestoreId 
                      ? 'bg-white/20 text-white' 
                      : msg.status === 'new' ? 'bg-blue-50 text-blue-600' : 'bg-black text-white'
                  }`}>
                    {getStatusBadge(msg.status).text}
                  </span>
                </div>
                <p className={`text-[10px] truncate mb-2 ${selectedMessage?.firestoreId === msg.firestoreId ? 'text-gray-300' : 'text-black'}`}>
                  {msg.email}
                </p>
                <div className="flex items-center justify-between">
                  <p className={`text-[9px] font-medium ${selectedMessage?.firestoreId === msg.firestoreId ? 'text-gray-400' : 'text-black'}`}>
                    {formatDate(msg.createdAt).split(' u ')[0]}
                  </p>
                  {msg.status === 'new' && selectedMessage?.firestoreId !== msg.firestoreId && (
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* MAIN - Detalji Poruke */}
      <div className={`flex-1 flex flex-col min-w-0 bg-gray-50/30 ${!selectedMessage ? 'hidden md:flex' : 'flex'}`}>
        {selectedMessage ? (
          <>
            {/* Toolbar */}
            <div className="p-6 bg-white border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => navigate('/admin/messages')}
                  className="md:hidden p-2 text-black hover:bg-gray-100 rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="flex flex-col">
                  <h2 className="text-xl font-black text-black uppercase tracking-tight truncate max-w-[200px] sm:max-w-md">
                    {selectedMessage.name}
                  </h2>
                  <p className="text-[10px] text-black font-bold uppercase tracking-widest">Detalji poruke</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <select
                  value={selectedMessage.status}
                  onChange={(e) => updateMessageStatus(selectedMessage.firestoreId, e.target.value)}
                  className="px-3 py-2 bg-gray-50 border border-gray-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-black outline-none hover:border-black transition-all cursor-pointer"
                >
                  <option value="new">Novo</option>
                  <option value="read">Pročitano</option>
                  <option value="replied">Odgovoreno</option>
                </select>
                <button
                  onClick={() => deleteMessage(selectedMessage.firestoreId)}
                  className="p-2.5 text-black hover:text-red-500 bg-gray-50 border border-gray-100 rounded-xl transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6 md:p-10">
              <div className="max-w-3xl mx-auto space-y-8">
                
                {/* Header Info Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <span className="text-[9px] font-black text-black uppercase tracking-widest block mb-2">KLIJENT INFO</span>
                    <p className="text-sm font-bold text-black mb-1">{selectedMessage.name}</p>
                    <p className="text-xs text-black font-medium">{selectedMessage.email}</p>
                  </div>
                  <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <span className="text-[9px] font-black text-black uppercase tracking-widest block mb-2">DOGAĐAJ / DATUM</span>
                    <p className="text-sm font-bold text-black mb-1">{selectedMessage.weddingDate || 'Nije navedeno'}</p>
                    <p className="text-xs text-black font-medium">{formatDate(selectedMessage.createdAt)}</p>
                  </div>
                </div>

                {/* Tags Section */}
                {(selectedMessage.packageName || (selectedMessage.contactMethods && selectedMessage.contactMethods.length > 0)) && (
                   <div className="flex flex-wrap gap-3">
                      {selectedMessage.packageName && (
                        <div className="px-4 py-2 bg-black text-white text-[10px] font-black uppercase tracking-widest rounded-full">
                          📦 {selectedMessage.packageName}
                        </div>
                      )}
                      {selectedMessage.contactMethods?.map((method, idx) => (
                        <div key={idx} className="px-4 py-2 bg-white border border-gray-100 text-[10px] font-black uppercase tracking-widest rounded-full shadow-sm">
                          {method === 'email' && '📧 Email'}
                          {method === 'viber' && '💜 Viber'}
                          {method === 'whatsapp' && '💚 WhatsApp'}
                          {method === 'telegram' && '✈️ Telegram'}
                          {method === 'sms' && '📱 SMS'}
                        </div>
                      ))}
                   </div>
                )}

                {/* Message Body */}
                <div className="bg-white p-8 md:p-10 rounded-3xl border border-gray-100 shadow-sm relative">
                  <div className="absolute top-0 right-0 p-6 opacity-5 font-playfair text-6xl">"</div>
                  <span className="text-[9px] font-black text-black uppercase tracking-widest block mb-6 px-1 border-l-2 border-black ml-1">SADRŽAJ PORUKE</span>
                  <p className="text-black text-base leading-relaxed whitespace-pre-wrap font-medium">
                    {selectedMessage.message}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-4 pt-6">
                  <a
                    href={`mailto:${selectedMessage.email}?subject=Upit za vjenčanje - Nejira Zukić&body=Poštovani ${selectedMessage.name},%0D%0A%0D%0A`}
                    className="flex-1 min-w-[200px] flex items-center justify-center gap-3 bg-black text-white py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-gray-800 transition-all shadow-lg hover:shadow-black/20"
                  >
                    <span>ODGOVORI NA EMAIL</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                  {selectedMessage.phone && (
                    <a
                      href={`https://wa.me/${selectedMessage.phone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-8 border-2 border-green-500 text-green-600 font-black text-[10px] uppercase tracking-widest rounded-2xl hover:bg-green-50 transition-all"
                    >
                      WhatsApp
                    </a>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-black p-12">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-xs font-black uppercase tracking-widest text-black">Odaberite poruku za prikaz detalja</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMessages;
